import { Checkbox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { type FC, type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PencilIcon from 'src/assets/icons/PencilIcon';
import Button from 'src/components/design-system/Button';
import OptionsMenu, { OptionsMenuItem } from 'src/components/design-system/OptionsMenu';
import useConfirm from 'src/hooks/useConfirm';
import useRedirect from 'src/hooks/useRedirect';
import { formatShoppingListGlCodeDisplay } from 'src/services/favorite/mapFavoriteFolderToShoppingList';
import { ShoppingList } from 'src/types/view_shopping_lists/ShoppingList';
import { useContextSelector } from 'use-context-selector';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

interface ShoppingListCardProps {
  shoppingList: ShoppingList;
}

const ShoppingListCard: FC<ShoppingListCardProps> = ({ shoppingList }): ReactElement => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { title, owner, glCode, glDescription, remainingBalance, tags, productCount, isShare } =
    shoppingList;
  const formattedGlCode = formatShoppingListGlCodeDisplay(glCode, glDescription);
  const glCodeDisplay = formattedGlCode || t('Unassigned');
  const { getUrl, redirect } = useRedirect();
  const hasProducts = productCount > 0;

  const openEditShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openEditShoppingListPanel
  );
  const deleteShoppingList = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.deleteShoppingList
  );
  const deletingShoppingListId = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.deletingShoppingListId
  );
  const openShareShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openShareShoppingListPanel
  );
  const openCopyShoppingListPanel = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.openCopyShoppingListPanel
  );
  const selectedShoppingListIds = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.selectedShoppingListIds
  );
  const toggleShoppingListSelection = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.toggleShoppingListSelection
  );
  const canShareShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.canShareShoppingLists
  );
  const canCopyShoppingLists = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.canCopyShoppingLists
  );

  const isSelected = selectedShoppingListIds.includes(shoppingList.id);
  const isLockedList = isShare;
  const actionsDisabled = isLockedList;

  const handleEditClick = useCallback(() => {
    if (actionsDisabled) {
      return;
    }
    openEditShoppingListPanel(shoppingList);
  }, [actionsDisabled, openEditShoppingListPanel, shoppingList]);

  const handleViewProductsClick = useCallback(() => {
    if (!hasProducts) {
      return;
    }
    redirect(getUrl('viewShoppingListProducts')(shoppingList.id), false, false);
  }, [getUrl, hasProducts, redirect, shoppingList.id]);

  const handleDeleteClick = useCallback(async () => {
    if (actionsDisabled) {
      return;
    }

    const folderId = Number(shoppingList.id);
    if (!Number.isFinite(folderId)) {
      toast.error(t('Failed to delete shopping list'));
      return;
    }

    const isDeleteAccepted = await confirm({
      title: t('deleteConfirmationTitle'),
      content: (
        <p className="break-words">
          {t('Are you sure you want to delete {{listName}}?', {
            listName: title,
          })}
        </p>
      ),
      confirmButtonText: t('yesDelete'),
      cancelButtonText: t('noCancel'),
      width: '420px',
    });

    if (!isDeleteAccepted) {
      return;
    }

    try {
      await deleteShoppingList(folderId);
    } catch {
      toast.error(t('Failed to delete shopping list'));
    }
  }, [actionsDisabled, confirm, deleteShoppingList, shoppingList.id, t, title]);

  const isDeletingThisList = deletingShoppingListId === shoppingList.id;

  const menuOptions = useMemo(() => {
    const options: OptionsMenuItem[] = [];

    if (canCopyShoppingLists) {
      options.push({
        id: 'send-copy',
        label: t('Send a Copy'),
        onClick: () => openCopyShoppingListPanel(shoppingList),
      });
    }

    if (canShareShoppingLists) {
      options.push({
        id: 'share-list',
        label: t('Share List'),
        onClick: () => openShareShoppingListPanel(shoppingList),
      });
    }

    if (!isLockedList) {
      options.push({
        id: 'delete-list',
        label: t('Delete List'),
        onClick: handleDeleteClick,
        disabled: isDeletingThisList,
      });
    }

    return options;
  }, [
    canCopyShoppingLists,
    canShareShoppingLists,
    handleDeleteClick,
    isDeletingThisList,
    isLockedList,
    openCopyShoppingListPanel,
    openShareShoppingListPanel,
    shoppingList,
    t,
  ]);

  return (
    <article
      className={clsx(
        'flex h-full w-full min-w-0 flex-col rounded-sm bg-white px-3 pt-3 pb-0 shadow-sm',
        isSelected ? 'border-2 border-[#3FA9DB]' : 'border border-[#D1D1D1]'
      )}
      data-testid={`shopping-list-card-${shoppingList.id}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <Checkbox
          aria-label={t('Select list')}
          checked={isSelected}
          onChange={() => toggleShoppingListSelection(shoppingList.id)}
        />
        {!isLockedList || menuOptions.length > 0 ? (
          <div className="flex items-center gap-1">
            {!isLockedList ? (
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded border border-[#D1D1D1] bg-white text-info hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
                aria-label={t('Edit')}
                onClick={handleEditClick}
              >
                <PencilIcon />
              </button>
            ) : null}
            {menuOptions.length > 0 ? (
              <OptionsMenu options={menuOptions} ariaLabel={t('More options')} />
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <h3
          className="truncate text-sm font-semibold leading-tight text-[#111111] sm:text-base"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={title}
        >
          {title}
        </h3>

        <div className="flex flex-col gap-0.5 text-xs font-normal leading-tight sm:text-sm">
          <p className="truncate">
            <span className="text-[rgba(107,114,128,1)]">{t('Owner')}:</span>{' '}
            <span
              className="text-[rgba(0,0,0,1)]"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={owner}
            >
              {owner}
            </span>
          </p>
          <p className="truncate">
            <span className="text-[rgba(107,114,128,1)]">{t('GL Code')}:</span>{' '}
            <span
              className="text-[rgba(0,0,0,1)]"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={glCodeDisplay}
            >
              {glCodeDisplay}
            </span>
          </p>
          <p className="truncate">
            <span className="text-[rgba(107,114,128,1)]">{t('Remaining Forecast Balance')}:</span>{' '}
            <span
              className="text-[rgba(0,0,0,1)]"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={remainingBalance}
            >
              {remainingBalance}
            </span>
          </p>
        </div>

        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 pb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#3FA9DB33] px-3 py-1.5 text-[11px] font-normal leading-tight text-[rgba(0,0,0,1)] sm:text-xs"
              >
                {t(tag)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-auto w-full border-t border-[#D1D1D1] py-2">
        {hasProducts ? (
          <Button
            color="info"
            size="md"
            className="!m-0 w-full shrink-0 justify-center font-medium"
            onClick={handleViewProductsClick}
          >
            {t('View Products')} ({productCount})
          </Button>
        ) : (
          <span className="flex h-[30px] items-center justify-center text-xs font-medium text-[#9CA3AF] sm:text-sm">
            {t('No Products in this list')}
          </span>
        )}
      </div>
    </article>
  );
};

export default ShoppingListCard;
